// ============================================================
// ClientPicker — choisir un client en tapant les premières lettres.
// ------------------------------------------------------------
// Un `<select>` marche jusqu'à trente lignes. Au-delà, il faut faire
// défiler une liste alphabétique pour trouver « WELKENRAEDT MÉTAL », et
// le geste qui devrait prendre une seconde en prend dix.
//
// ── ET IL Y AVAIT PIRE QU'UNE GÊNE ──
//
// Les deux écrans qui choisissaient un client demandaient
// `?per_page=100`. Avec 199 clients actifs en base depuis l'import,
// **la moitié du fichier était invisible et impossible à choisir** — et
// rien ne le disait : la liste s'affichait, complète en apparence,
// simplement tronquée à la centième ligne alphabétique.
//
// C'est pour ça que ce composant interroge le SERVEUR à chaque frappe au
// lieu de filtrer une liste déjà chargée. Filtrer côté navigateur aurait
// donné la même impression de fonctionner, en cherchant dans un
// sous-ensemble arbitraire.
//
// ── UNE SÉLECTION EST UN IDENTIFIANT, PAS UN TEXTE ──
//
// Tant que l'utilisateur tape, il n'y a PAS de client choisi. Le texte
// saisi ne vaut jamais sélection, même s'il correspond exactement au nom
// d'un client : deux sociétés peuvent porter le même nom, et le fichier
// de Mertens en contenait justement un doublon. Choisir se fait au
// clavier ou à la souris, sur une ligne, et rien d'autre ne compte.
// ============================================================

import { useEffect, useId, useRef, useState } from 'react';
import { api } from '../lib/api.js';

/** Ce qu'on affiche à droite du nom : de quoi distinguer deux homonymes. */
function detail(c) {
  return [c.city, c.client_number].filter(Boolean).join(' · ');
}

/**
 * @param {object} p
 * @param {{id:number, company:string}|null} p.selected  le client courant.
 *   Il est passé PAR L'APPELANT et non retrouvé dans les résultats : le
 *   client d'un devis ouvert peut très bien ne figurer dans aucune
 *   recherche en cours, et il doit rester affiché.
 * @param {(client:object) => void} p.onSelect
 */
export function ClientPicker({
  label = 'Client',
  selected = null,
  disabled = false,
  required = false,
  actifsSeulement = true,
  hint = null,
  onSelect,
}) {
  const id = useId();
  const [saisie, setSaisie] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [resultats, setResultats] = useState([]);
  const [surligne, setSurligne] = useState(-1);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const boite = useRef(null);

  // ── La recherche, DIFFÉRÉE ──
  //
  // 180 ms : assez pour qu'une frappe normale n'envoie qu'une requête
  // par mot, assez court pour que la liste paraisse suivre les doigts.
  // Sans ce délai, taper « WELKENRAEDT » lancerait onze requêtes dont
  // dix seraient périmées à l'arrivée.
  useEffect(() => {
    if (!ouvert) return undefined;
    let vivant = true;
    const t = setTimeout(async () => {
      setChargement(true);
      setErreur(null);
      try {
        const params = new URLSearchParams({ per_page: '20' });
        if (saisie.trim()) params.set('q', saisie.trim());
        if (actifsSeulement) params.set('active', 'true');
        const res = await api.get(`/clients?${params}`);
        if (!vivant) return;
        setResultats(res.rows);
        setSurligne(res.rows.length ? 0 : -1);
      } catch (err) {
        if (vivant) setErreur(err);
      } finally {
        if (vivant) setChargement(false);
      }
    }, 180);
    return () => {
      vivant = false;
      clearTimeout(t);
    };
  }, [saisie, ouvert, actifsSeulement]);

  // Un clic ailleurs referme. Sans ça, la liste reste ouverte par-dessus
  // le reste du formulaire et masque ce qu'on veut remplir ensuite.
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e) => {
      if (boite.current && !boite.current.contains(e.target)) fermer();
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  function fermer() {
    setOuvert(false);
    setSaisie('');
    setSurligne(-1);
  }

  function choisir(client) {
    onSelect(client);
    fermer();
  }

  function auClavier(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!ouvert) return setOuvert(true);
      const pas = e.key === 'ArrowDown' ? 1 : -1;
      return setSurligne((i) => {
        if (!resultats.length) return -1;
        return (i + pas + resultats.length) % resultats.length;
      });
    }
    if (e.key === 'Enter') {
      // On n'intercepte Entrée QUE si une ligne est surlignée : sinon on
      // empêcherait la validation normale du formulaire.
      if (ouvert && resultats[surligne]) {
        e.preventDefault();
        choisir(resultats[surligne]);
      }
      return undefined;
    }
    if (e.key === 'Escape' && ouvert) {
      e.preventDefault();
      return fermer();
    }
    return undefined;
  }

  return (
    <div className="picker" ref={boite}>
      <label className="field__label" htmlFor={`${id}-input`}>
        {label}
        {required ? ' *' : ''}
      </label>

      <input
        id={`${id}-input`}
        className="input picker__input"
        type="text"
        role="combobox"
        aria-expanded={ouvert}
        aria-controls={`${id}-liste`}
        aria-autocomplete="list"
        // L'étiquette est déjà associée par `htmlFor`. On la répète ici
        // parce que `jsx-a11y` — en ERREUR sur ce projet — ne reconnaît
        // pas l'association sur un rôle personnalisé, et parce qu'un
        // libellé explicite ne coûte rien à un lecteur d'écran.
        aria-label={label}
        aria-activedescendant={ouvert && surligne >= 0 ? `${id}-opt-${surligne}` : undefined}
        autoComplete="off"
        disabled={disabled}
        // Quand rien n'est ouvert, le champ AFFICHE le client choisi.
        // Dès qu'on tape, il devient une zone de recherche : c'est le
        // même champ, et c'est ce qui rend le geste évident.
        value={ouvert ? saisie : (selected?.company ?? '')}
        placeholder={selected ? 'Changer de client…' : 'Tapez les premières lettres…'}
        onFocus={() => !disabled && setOuvert(true)}
        onChange={(e) => {
          setOuvert(true);
          setSaisie(e.target.value);
        }}
        onKeyDown={auClavier}
      />

      {/* ── LA LISTE N'EXISTE QUE SI ELLE A DES OPTIONS ──
          Un `role="listbox"` qui contiendrait « Recherche… » ou
          « aucun résultat » annoncerait ces phrases comme des choix
          sélectionnables. Les messages vivent donc HORS de la liste, en
          `role="status"` — un lecteur d'écran les entend arriver sans
          les confondre avec des options. */}
      {ouvert && resultats.length ? (
        <div className="picker__liste" id={`${id}-liste`} role="listbox" aria-label={label}>
          {resultats.map((c, i) => (
              <button
                key={c.id}
                type="button"
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={i === surligne}
                className={`picker__ligne${i === surligne ? ' picker__ligne--actif' : ''}`}
                // `mousedown` et non `click` : le `blur` du champ
                // partirait avant le clic et refermerait la liste sous
                // le doigt.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choisir(c);
                }}
                onMouseEnter={() => setSurligne(i)}
              >
                <span className="picker__nom">{c.company}</span>
                {detail(c) ? <span className="picker__detail">{detail(c)}</span> : null}
              </button>
          ))}
        </div>
      ) : null}

      {ouvert && !resultats.length ? (
        <p className="picker__liste picker__vide" role="status">
          {erreur
            ? erreur.message
            : chargement
              ? 'Recherche…'
              : saisie.trim()
                ? `Aucun client ne correspond à « ${saisie.trim()} ».`
                : 'Aucun client.'}
        </p>
      ) : null}

      {/* L'indication disparaît dès que la liste s'ouvre : elle dit quoi
          faire, et une fois qu'on le fait elle ne sert plus qu'à
          repousser les résultats de deux lignes vers le bas. */}
      {hint && !ouvert ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

export default ClientPicker;
